import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Command } from "cmdk";
import {
  Search, Sparkles, ArrowRight, ClipboardList, Bell, Settings2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_BASE, getNavForRole, type Role } from "./nav-config";

interface HeaderSearchProps {
  className?: string;
  /** Mobile reveal: focus the input on mount and open the results immediately. */
  autoFocus?: boolean;
  /** Mobile reveal: dismiss the search (called on navigate, Escape, or outside click). */
  onClose?: () => void;
}

export function HeaderSearch({ className, autoFocus, onClose }: HeaderSearchProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = (user?.role ?? "vessel") as Role;
  const base = ROLE_BASE[role];
  const groups = React.useMemo(() => getNavForRole(role), [role]);

  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Mobile reveal: focus the input and open results as soon as it mounts.
  React.useEffect(() => {
    if (!autoFocus) return;
    inputRef.current?.focus();
    setOpen(true);
  }, [autoFocus]);

  // ⌘K focuses the inline input rather than opening a modal.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Close when clicking outside the search bar.
  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        onClose?.();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, onClose]);

  const go = (to: string) => {
    setOpen(false);
    setQuery("");
    inputRef.current?.blur();
    onClose?.();
    navigate(to);
  };

  return (
    <div
      ref={containerRef}
      className={cn("relative", className)}
    >
      <Command
        shouldFilter
        className="bg-transparent"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setOpen(false);
            inputRef.current?.blur();
            onClose?.();
          }
        }}
      >
        <div
          className={cn(
            "flex items-center gap-2 h-9 px-3",
            "bg-surface border border-border rounded-md",
            "text-fg-subtle transition-colors",
            "hover:border-border-strong",
            "focus-within:ring-2 focus-within:ring-ring/40 focus-within:border-border-strong",
          )}
        >
          <Search className="size-4 shrink-0" />
          <Command.Input
            ref={inputRef}
            value={query}
            onValueChange={setQuery}
            onFocus={() => setOpen(true)}
            placeholder="Search vessels, inspections, observations…"
            className={cn(
              "flex-1 bg-transparent text-sm outline-none border-0 p-0",
              "placeholder:text-fg-subtle text-fg",
            )}
          />
          <kbd className="hidden lg:inline-flex items-center gap-0.5 text-[10px] text-fg-subtle px-1.5 py-0.5 border border-border-subtle rounded">
            ⌘K
          </kbd>
        </div>

        {open ? (
          <div
            className={cn(
              "absolute left-0 right-0 top-full mt-1 z-50",
              "rounded-md border border-border bg-surface-elevated text-fg shadow-md overflow-hidden",
            )}
          >
            <Command.List className="max-h-[400px] overflow-y-auto p-2">
              <Command.Empty className="py-8 text-center text-sm text-fg-muted">
                No results found.
              </Command.Empty>

              <Item
                onSelect={() => go(`${base}/ai`)}
                icon={<Sparkles className="size-4 text-ai-from" />}
                label="Ask AI about your fleet"
                hint="Open AI insights"
              />

              {groups.flatMap((g) =>
                g.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Item
                      key={item.to}
                      onSelect={() => go(item.to)}
                      icon={<Icon className={cn("size-4 text-fg-subtle", item.ai && "text-ai-from")} />}
                      label={item.label}
                    />
                  );
                }),
              )}

              <Item
                onSelect={() => go(`${base}/manage-inspections`)}
                icon={<ClipboardList className="size-4 text-fg-subtle" />}
                label="New inspection"
              />
              <Item
                onSelect={() => go(`${base}/notifications`)}
                icon={<Bell className="size-4 text-fg-subtle" />}
                label="View notifications"
              />
              <Item
                onSelect={() => go(`${base}/profile`)}
                icon={<Settings2 className="size-4 text-fg-subtle" />}
                label="Settings"
              />
            </Command.List>
          </div>
        ) : null}
      </Command>
    </div>
  );
}

function Item({
  onSelect, icon, label, hint,
}: {
  onSelect: () => void;
  icon: React.ReactNode;
  label: string;
  hint?: string;
}) {
  return (
    <Command.Item
      onSelect={onSelect}
      className={cn(
        "flex items-center gap-2.5 px-2 py-2 rounded-md text-sm cursor-pointer select-none",
        "data-[selected=true]:bg-surface-hover data-[selected=true]:text-fg",
      )}
    >
      {icon}
      <span className="flex-1 truncate">{label}</span>
      {hint ? <span className="text-[10px] text-fg-subtle">{hint}</span> : null}
      <ArrowRight className="size-3 text-fg-subtle opacity-0 data-[selected=true]:opacity-100" />
    </Command.Item>
  );
}
